import React from 'react';
import PropTypes from 'prop-types';
import useMedia from 'use-media';
import path from 'path';
import {css} from '@emotion/react';
import {graphql, useStaticQuery} from 'gatsby';
import {useLocation} from '@reach/router';
import AnnouncementBanner from './AnnouncementBanner';
import ExternalLink from './ExternalLink';
import Link from './Link';
import Button from './Button';
import Dropdown from './Dropdown';
import Icon from './Icon';
import GlobalNavLink from './GlobalNavLink';
import SearchInput from './SearchInput';
import SearchModal from './SearchModal';
import useQueryParams from '../hooks/useQueryParams';
import useThemeTranslation from '../hooks/useThemeTranslation';
import useHasMounted from '../hooks/useHasMounted';
import useInstrumentedHandler from '../hooks/useInstrumentedHandler';

import ComputoptLogo from '../images/Computop_Logo.png';

export const NR_SITES = {
    DOCS: 'DOCS', DEVELOPER: 'DEVELOPER', COMMUNITY: 'COMMUNITY', LEARN: 'LEARN',
};

const HEADER_LINKS = new Map();

HEADER_LINKS.set(NR_SITES.DOCS, {
    text: 'Docs', href: 'https://docs.newrelic.com/',
})
    .set(NR_SITES.DEVELOPER, {
        text: 'Developer', href: 'https://developer.newrelic.com/',
    })
    .set(NR_SITES.COMMUNITY, {
        text: 'Community', href: 'https://discuss.newrelic.com/',
    })
    .set(NR_SITES.LEARN, {
        text: 'Learn', href: 'https://learn.newrelic.com/',
    });

const createNavList = (listType, activeSite = null) => {
    const navList = [];
    HEADER_LINKS.forEach(({text, href}) => {
        switch (listType) {
            case 'main':
                navList.push(<li key={href}>
                    <GlobalNavLink
                        href={href}
                        activeSite={activeSite && HEADER_LINKS.get(activeSite)}
                        instrumentation={{
                            component: 'globalHeader', layoutElement: 'globalHeader',
                        }}
                    >
                        {text}
                    </GlobalNavLink>
                </li>);
                break;
            case 'dropdown':
                navList.push(<Dropdown.MenuItem key={href} href={href}>
                    {text}
                </Dropdown.MenuItem>);
                break;
        }
    });
    return navList;
};

// removes the site nav from the header in favor of the search bar
// swaps out logo into collapsable nav
const NAV_BREAKPOINT = '1070px';
const LOGO_TEXT_BREAKPOINT = '460px';
const LAYOUT_BREAKPOINT = '1150px';

const GlobalHeader = ({className, activeSite, hideSearch = false}) => {
    const hasMounted = useHasMounted();
    const location = useLocation();
    const {queryParams, setQueryParam, deleteQueryParam} = useQueryParams();
    const {t} = useThemeTranslation();

    const {
        allLocale: {nodes: locales}, site: {
            layout: {mobileBreakpoint},
        },
    } = useStaticQuery(graphql`
    query GlobalHeaderQuery {
      allLocale(sort: [{ isDefault: DESC }, { locale: ASC }]) {
        nodes {
          locale
          localName
          isDefault
        }
      }
      site {
        layout {
          mobileBreakpoint
        }
      }
    }
  `);

    const hideLogoText = useMedia({maxWidth: LOGO_TEXT_BREAKPOINT});

    const matchLocalePath = new RegExp(`^\\/(${locales.map(({locale}) => locale).join('|')})`);

    const handleLocaleClick = useInstrumentedHandler(null, ({locale}) => ({
        eventName: 'localeDropDownClick',
        category: 'LocaleDropDown',
        origin: 'gatsbyTheme',
        layoutElement: 'globalHeader',
        locale,
    }));

    return (<>
            <SearchModal
                onClose={() => {
                    deleteQueryParam('q');
                }}
                isOpen={hasMounted && queryParams.has('q')}
            />
            <AnnouncementBanner/>
            <div
                data-swiftype-index={false}
                className={className}
                css={css`
                display: grid;
                background-color: var(--erno-black);
                font-weight: 700;
                color: #fff;
                font-size: .875rem;
                text-transformation: uppercase;
                grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
                grid-template-rows: 1fr auto;
                grid-template-areas: 'logo nav';
                box-shadow: var(--shadow-2);
                position: sticky;
                top: 0;
                z-index: 80;
                height: var(--global-header-height);
                @media screen and (max-width: ${LAYOUT_BREAKPOINT}) and (min-width: ${NAV_BREAKPOINT}) {
                    grid-template-columns: calc(150px + var(--site-content-padding)) minmax(
                0,
                1fr
              );
                }
                @media screen and (max-width: ${mobileBreakpoint}) {
                    grid-template-columns: calc(150px + var(--site-content-padding)) minmax(
                0,
                1fr
              );
                }
            `}
            >
                <nav
                    css={css`
                    grid-area: logo;
                    padding: 0 var(--site-content-padding);
                    display: flex;
                    align-items: center;
                    height: 100%;
                    overflow: hidden;
                `}
                >
                    <ExternalLink
                        href="https://developer.computop.com/" // TODO change
                        css={css`
                        display: flex;
                        align-items: center;
                    `}
                        instrumentation={{
                            component: 'globalHeaderLogo', layoutElement: 'globalHeader',
                        }}
                    >
                        <img
                            src={ComputoptLogo}
                        />
                    </ExternalLink>
                </nav>
                <ul
                    css={css`
                    margin: 0;
                    margin-left: 1rem;
                    padding: 0;
                    display: flex;
                    list-style-type: none;
                    align-items: center;
                    justify-content: flex-end;
                    > li {
                        transition: all 0.2s ease-out;
                        &:not(:first-of-type) &:not(:last-of-type) {
                            margin-left: 0.5rem;
                        }
                    }
                    @media screen and (max-width: ${NAV_BREAKPOINT}) {
                        width: 100%;
                        justify-content: flex-end;
                        margin: 0;
                    }
                `}>
                    <li
                        css={css`
                        margin: 0rem 1rem;
                        width: 100%;
                        max-width: 320px;

                        @media screen and (max-width: 930px) {
                            margin-right: 1rem;
                        }
                        @media screen and (max-width: ${NAV_BREAKPOINT}) {
                            margin-left: 0;
                        }
                        @media screen and (max-width: ${mobileBreakpoint}) {
                            display: none;
                        }
                    `}>
                        <div
                            css={css`
                            grid-area: nav;
                            display: flex;
                            justify-content: space-between;
                            padding: 0 1.5rem;
                            width: 100%;
                            max-width: var(--site-max-width);
                            margin: auto;
                        `}>
                            <div css={css`
                                 position: relative;
                                 display: inline-block;
                                
                                &:hover div {display: block}
                            `}>
                                <button css={css`
                                    margin: 0;
                                    border-radius: 0px;
                                    font-size: 0.75rem;
                                    color: var(--system-text-primary-dark);
                                    background: transparent;
                                    color: white;
                                    border: none;
                                    size:{Button.SIZE:EXTRA_SMALL}
                                `}>
                                    <Icon name="nr-i18n" size="20px" />
                                </button>
                                <div css={css`
                                    display: none;
                                    position: absolute;
                                    background-color: #f1f1f1;
                                    min-width: 160px;
                                    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
                                    z-index: 1;
                                    border-radius: 4px;
                                `}>
                                    <a css={css`
                                        color: black;
                                        padding: 12px 16px;
                                        text-decoration: none;
                                        display: block;
                                        border-radius: 4px;
                                        
                                        &:hover {
                                            background-color: #ddd;
                                            color: var(--nav-highlight)
                                        }
                                    `}

                                       href="/de/">
                                        Deutsch
                                    </a>
                                    <a css={css`
                                        color: black;
                                        padding: 12px 16px;
                                        text-decoration: none;
                                        display: block;
                                        border-radius: 4px;

                                        &:hover {
                                            background-color: #ddd;
                                            color: var(--nav-highlight)
                                        }
                                    `}
                                       href="/en/">
                                        English
                                    </a>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
    </>);
};

GlobalHeader.propTypes = {
    className: PropTypes.string, activeSite: PropTypes.oneOf(Object.values(NR_SITES)), hideSearch: PropTypes.bool,
};

export default GlobalHeader;
